import { useState, useMemo } from 'react';
import { Card, CardContent } from '@client/components/ui/card';
import { Button } from '@client/components/ui/button';
import { Checkbox } from '@client/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@client/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@client/components/ui/dropdown-menu';
import { UserMinus, Trash2, X, Send, MoreHorizontal, Calendar, RefreshCw } from 'lucide-react';
import { EditSeatExpirationDialog } from './EditSeatExpirationDialog';
import { useFeatureFlags } from '@client/contexts/FeatureFlagsContext';
import { format } from 'date-fns';
import { Badge } from '@client/components/ui/badge';

type EnrichedSubscriptionSeat = SubscriptionSeat & {
  advisorEmail: string | null;
  advisorName: string | null;
};

interface SeatsTableProps {
  seats: EnrichedSubscriptionSeat[];
  subscription: any;
  isOwner: boolean;
  isMutating: boolean;
  isFetching: boolean;
  currentMutation: string | null;
  onRemoveSeat: (seatId: string) => void;
  onResendInvitation: (seatId: string, email: string) => void;
  onRefresh: () => void;
}

type SeatAction = {
  key: string;
  label: string;
  icon: typeof Trash2;
  onClick: () => void;
  disabled?: boolean;
};

export function SeatsTable({
  seats,
  subscription,
  isOwner,
  isMutating,
  isFetching,
  currentMutation,
  onRemoveSeat,
  onResendInvitation,
  onRefresh,
}: SeatsTableProps) {
  const { flags } = useFeatureFlags();
  const [showAllStatuses, setShowAllStatuses] = useState(false);
  const [editExpirationDialogOpen, setEditExpirationDialogOpen] = useState(false);
  const [selectedSeatForExpiration, setSelectedSeatForExpiration] =
    useState<EnrichedSubscriptionSeat | null>(null);

  const calculateMonths = (assignedDateTime: Date | null): number | string => {
    if (!assignedDateTime) return '—';
    const assignedDate = new Date(assignedDateTime);
    const today = new Date();
    const yearsDiff = today.getFullYear() - assignedDate.getFullYear();
    const monthsDiff = today.getMonth() - assignedDate.getMonth();
    const totalMonths = yearsDiff * 12 + monthsDiff;
    return Math.floor(totalMonths);
  };

  const handleOpenExpirationDialog = (seat: EnrichedSubscriptionSeat) => {
    setSelectedSeatForExpiration(seat);
    setEditExpirationDialogOpen(true);
  };

  const handleCloseExpirationDialog = () => {
    setEditExpirationDialogOpen(false);
    setSelectedSeatForExpiration(null);
  };

  const getSeatActions = (seat: EnrichedSubscriptionSeat): SeatAction[] => {
    const actions: SeatAction[] = [];
    // Add edit expiration for active and invited seats (only if feature flag is enabled)
    if (
      flags.seatExpiration &&
      seat.advisorId !== subscription?.ownerAdvisorId &&
      (seat.status === 'active' || seat.status === 'invited')
    ) {
      actions.push({
        key: 'edit-expiration',
        label: 'Edit expiration',
        icon: Calendar,
        onClick: () => handleOpenExpirationDialog(seat),
      });
    }

    if (seat.status === 'inactive' && !seat.advisorId) {
      actions.push({
        key: 'remove-inactive',
        label: 'Remove seat',
        icon: Trash2,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    if (seat.status === 'invited') {
      actions.push(
        {
          key: 'resend-invitation',
          label: 'Resend invitation',
          icon: Send,
          onClick: () => onResendInvitation(seat.subscriptionSeatId, seat.invitedEmail || ''),
          disabled: isMutating && currentMutation === 'invite',
        },
        {
          key: 'cancel-invitation',
          label: 'Cancel invitation',
          icon: X,
          onClick: () => onRemoveSeat(seat.subscriptionSeatId),
        },
      );
    }

    if (seat.status === 'payment_failed') {
      actions.push({
        key: 'remove-failed',
        label: 'Remove failed seat',
        icon: X,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    if (seat.status === 'active' && seat.advisorId !== subscription?.ownerAdvisorId) {
      actions.push({
        key: 'remove-active',
        label: 'Remove seat',
        icon: UserMinus,
        onClick: () => onRemoveSeat(seat.subscriptionSeatId),
      });
    }

    return actions;
  };

  const sortedSeats = useMemo(() => {
    return seats
      .filter(seat => {
        if (showAllStatuses) return true;
        return (
          seat.status === 'invited' ||
          seat.status === 'active' ||
          seat.status === 'payment_failed' ||
          seat.status === 'paused'
        );
      })
      .sort((a, b) => {
        // First sort by status: active comes first
        if (a.status === 'active' && b.status !== 'active') return -1;
        if (a.status !== 'active' && b.status === 'active') return 1;

        // Then sort by assignedDateTime: most recent first
        const dateA = a.assignedDateTime ? new Date(a.assignedDateTime).getTime() : 0;
        const dateB = b.assignedDateTime ? new Date(b.assignedDateTime).getTime() : 0;
        return dateB - dateA;
      });
  }, [seats, showAllStatuses]);

  return (
    <Card className="border-primary-200 mt-6 max-w-4xl border">
      <CardContent className="p-6">
        <div className="mb-1 flex items-center justify-between">
          <h3 className="text-lg font-semibold text-gray-700">Subscription Seats</h3>
          <Button
            variant="ghost"
            size="icon"
            onClick={onRefresh}
            disabled={isFetching}
            className="h-6 w-6 p-0 text-gray-600"
          >
            <RefreshCw className={`h-4 w-4 ${isFetching ? 'animate-spin' : ''}`} />
            <span className="sr-only text-gray-700">Refresh</span>
          </Button>
        </div>
        <p className="mb-4 text-sm text-gray-500">
          View and manage all users currently assigned to seats under your subscription.
        </p>
        <div className="mb-4 flex items-center justify-end gap-2">
          <Checkbox
            id="show-all-statuses"
            checked={showAllStatuses}
            onCheckedChange={checked => setShowAllStatuses(checked === true)}
          />
          <label
            htmlFor="show-all-statuses"
            className="cursor-pointer text-sm leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
          >
            Show all statuses
          </label>
        </div>
        <div className="max-h-[350px] overflow-y-auto">
          <Table>
            <TableHeader className="sticky top-0 z-10 bg-white">
              <TableRow>
                <TableHead>Status</TableHead>
                <TableHead>User</TableHead>
                <TableHead>Assigned Date</TableHead>
                <TableHead>Months Active</TableHead>
                <TableHead>Expiration Date</TableHead>
                {isOwner && <TableHead className="text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedSeats.map(seat => (
                <TableRow key={seat.subscriptionSeatId}>
                  <TableCell>
                    <span
                      className={`text-xs font-medium ${
                        seat.status === 'active'
                          ? 'text-green-700'
                          : seat.status === 'invited'
                            ? 'text-gray-600'
                            : seat.status === 'invite_declined' || seat.status === 'payment_failed'
                              ? 'text-red-700'
                              : 'text-gray-600'
                      }`}
                    >
                      {seat.status.includes('_') ? seat.status.split('_').join(' ') : seat.status}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex flex-col">
                      <div className="flex items-center gap-2">
                        <span>{seat.advisorName || '—'}</span>
                        {seat.advisorId === subscription?.ownerAdvisorId && (
                          <Badge
                            variant="secondary"
                            className="text-xs font-normal"
                          >
                            Owner
                          </Badge>
                        )}
                      </div>
                      <span className="text-sm text-gray-500">
                        {seat.advisorEmail || seat.invitedEmail || '—'}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell>
                    {seat.assignedDateTime
                      ? format(new Date(seat.assignedDateTime), 'M/d/yyyy')
                      : '—'}
                  </TableCell>
                  <TableCell>{calculateMonths(seat.assignedDateTime)}</TableCell>
                  <TableCell>
                    {seat.expiresDateTime
                      ? format(new Date(seat.expiresDateTime), 'M/d/yyyy')
                      : '—'}
                  </TableCell>
                  {isOwner && (
                    <TableCell className="text-right">
                      {(() => {
                        const actions = getSeatActions(seat);
                        if (actions.length === 0) return null;

                        return (
                          <DropdownMenu modal={false}>
                            <DropdownMenuTrigger asChild>
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-8 w-8 p-0"
                              >
                                <MoreHorizontal className="h-4 w-4" />
                                <span className="sr-only">Open actions menu</span>
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {actions.map(action => {
                                const Icon = action.icon;
                                return (
                                  <DropdownMenuItem
                                    key={action.key}
                                    onClick={action.onClick}
                                    disabled={action.disabled}
                                  >
                                    <Icon className="mr-2 h-4 w-4" />
                                    {action.label}
                                  </DropdownMenuItem>
                                );
                              })}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        );
                      })()}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
      {selectedSeatForExpiration && (
        <EditSeatExpirationDialog
          open={editExpirationDialogOpen}
          onClose={handleCloseExpirationDialog}
          seat={selectedSeatForExpiration}
          initialExpiresDateTime={selectedSeatForExpiration.expiresDateTime}
        />
      )}
    </Card>
  );
}
